import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// Page 404 publique — mais utile.
// Au lieu d'une page vide, on transforme l'impasse en RECHERCHE INTERNE
// DU CATALOGUE : on déduit un mot-clé de l'URL demandée, on lance la
// recherche toute seule et on propose des résultats réels + des raccourcis
// vers les grandes rubriques. Le visiteur perdu est rattrapé.
// ─────────────────────────────────────────────────────────────

interface ProductHit {
  id: number;
  name: string;
  description: string | null;
  price: number;
  discount_enabled: boolean;
  discount_price: number | null;
  media: unknown;
}

interface ServiceHit {
  id: number;
  titre: string;
  description: string | null;
  product_image: string | null;
}

/** Déduit un mot-clé lisible à partir du chemin demandé (ex: /perche-a-selfie). */
function keywordFromPath(path: string): string {
  const seg = path.split('/').filter(Boolean).pop() || '';
  try {
    return decodeURIComponent(seg)
      .replace(/[-_]+/g, ' ')
      .replace(/\.[a-z0-9]{1,5}$/i, '')
      .trim();
  } catch {
    return seg.replace(/[-_]+/g, ' ').trim();
  }
}

function firstImage(media: unknown): string {
  if (!media) return '';
  if (typeof media === 'string') return media;
  if (Array.isArray(media)) {
    const first = media[0];
    if (typeof first === 'string') return first;
    if (first && typeof first === 'object' && 'url' in first) {
      return String((first as { url?: string }).url || '');
    }
  }
  return '';
}

function serviceCover(productImage: string | null): string {
  if (!productImage) return '';
  return (
    productImage
      .split('|')
      .filter(Boolean)
      .filter((url) => {
        const lower = url.toLowerCase();
        return !(lower.endsWith('.mp4') || lower.endsWith('.webm') || lower.endsWith('.mov'));
      })[0] || ''
  );
}

const QUICK_LINKS = [
  { to: '/produits', icon: 'ri-shopping-bag-3-line', label: 'Produits' },
  { to: '/services', icon: 'ri-service-line', label: 'Services' },
  { to: '/hebergements', icon: 'ri-hotel-line', label: 'Hébergements' },
  { to: '/booking', icon: 'ri-calendar-check-line', label: 'Réserver' },
];

export default function NotFound() {
  const location = useLocation();
  const path = location.pathname;

  const [query, setQuery] = useState(() => keywordFromPath(path));
  const [products, setProducts] = useState<ProductHit[]>([]);
  const [services, setServices] = useState<ServiceHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const runSearch = useCallback(async (q: string) => {
    const term = q.trim();
    setSearched(true);
    if (term.length < 2) {
      setProducts([]);
      setServices([]);
      return;
    }
    setLoading(true);
    try {
      const [prodRes, svcRes] = await Promise.all([
        supabase
          .from('product_items')
          .select('id, name, description, price, discount_enabled, discount_price, media')
          .eq('status', 'active')
          .or(`name.ilike.%${term}%,description.ilike.%${term}%`)
          .limit(8),
        supabase
          .from('nospartenairesservices')
          .select('id, titre, description, product_image')
          .or(`titre.ilike.%${term}%,description.ilike.%${term}%`)
          .limit(6),
      ]);
      setProducts((prodRes.data as ProductHit[]) || []);
      setServices((svcRes.data as ServiceHit[]) || []);
    } catch {
      setProducts([]);
      setServices([]);
    }
    setLoading(false);
  }, []);

  // Recherche automatique dès l'arrivée, à partir du mot-clé déduit de l'URL.
  useEffect(() => {
    if (query.trim().length >= 2) runSearch(query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const total = products.length + services.length;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    runSearch(q);
  };

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      {/* En-tête */}
      <header className="border-b border-background-200/70">
        <div className="max-w-6xl mx-auto px-4 md:px-6 h-14 md:h-16 flex items-center justify-between">
          <Link
            to="/"
            className="text-lg font-bold font-heading text-foreground-950 hover:text-primary-600 transition-colors"
          >
            Zifek
          </Link>
          <Link
            to="/"
            className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-4-line"></i>
            Accueil
          </Link>
        </div>
      </header>

      {/* Contenu */}
      <main className="flex-1">
        <section className="px-4 md:px-6 pt-12 md:pt-16 pb-6">
          <div className="max-w-3xl mx-auto text-center">
            <div className="relative mb-4 inline-block">
              <span className="block text-[6rem] md:text-[8rem] leading-none font-black font-heading text-background-200 select-none">
                404
              </span>
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="w-14 h-14 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-compass-3-line text-2xl text-accent-600"></i>
                </span>
              </span>
            </div>

            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">
              Cette page n&apos;existe pas
            </h1>
            <p className="text-sm text-foreground-500 leading-relaxed max-w-xl mx-auto">
              L&apos;adresse demandée est introuvable, mais pas de panique : cherchez
              directement votre produit ou service dans le catalogue ci-dessous.
            </p>
            {path && path !== '/' && (
              <p className="text-xs text-foreground-400 font-mono break-all mt-3">{path}</p>
            )}

            {/* Recherche catalogue */}
            <form onSubmit={handleSubmit} className="mt-6 max-w-xl mx-auto">
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                  <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"></i>
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Rechercher un produit, un service..."
                    className="w-full pl-11 pr-4 py-3 bg-background-50 border border-background-200/70 rounded-full text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                  />
                </div>
                <button
                  type="submit"
                  className="px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-bold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer flex items-center justify-center gap-2"
                >
                  <i className="ri-search-line"></i>
                  Rechercher
                </button>
              </div>
            </form>
          </div>
        </section>

        {/* Résultats */}
        <section className="px-4 md:px-6 pb-16">
          <div className="max-w-5xl mx-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
              </div>
            ) : total > 0 ? (
              <div className="space-y-10">
                {products.length > 0 && (
                  <div>
                    <h2 className="text-base md:text-lg font-bold font-heading text-foreground-950 mb-4">
                      <i className="ri-shopping-bag-line mr-2 text-primary-500"></i>
                      Produits ({products.length})
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {products.map((p) => {
                        const img = firstImage(p.media);
                        const promo = p.discount_enabled && p.discount_price != null;
                        return (
                          <Link
                            key={p.id}
                            to={`/product/${p.id}`}
                            className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-background-300/60 transition-all no-underline"
                          >
                            <div className="h-40 bg-background-100 overflow-hidden">
                              {img ? (
                                <img src={img} alt={p.name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <i className="ri-shopping-bag-line text-2xl text-foreground-300"></i>
                                </div>
                              )}
                            </div>
                            <div className="p-3">
                              <p className="text-sm font-semibold text-foreground-900 line-clamp-2 mb-1">{p.name}</p>
                              {promo && p.discount_price != null ? (
                                <span className="text-sm font-bold text-primary-600">
                                  {Number(p.discount_price).toLocaleString()} MAD
                                </span>
                              ) : (
                                <span className="text-sm font-bold text-primary-600">
                                  {Number(p.price || 0).toLocaleString()} MAD
                                </span>
                              )}
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}

                {services.length > 0 && (
                  <div>
                    <h2 className="text-base md:text-lg font-bold font-heading text-foreground-950 mb-4">
                      <i className="ri-service-line mr-2 text-primary-500"></i>
                      Services ({services.length})
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {services.map((s) => {
                        const cover = serviceCover(s.product_image);
                        return (
                          <Link
                            key={s.id}
                            to={`/service/${s.id}`}
                            className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-background-300/60 transition-all no-underline"
                          >
                            <div className="h-36 bg-background-100 overflow-hidden">
                              {cover ? (
                                <img src={cover} alt={s.titre} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <i className="ri-service-line text-2xl text-foreground-300"></i>
                                </div>
                              )}
                            </div>
                            <div className="p-3">
                              <p className="text-sm font-semibold text-foreground-900 line-clamp-2">{s.titre}</p>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="text-center">
                  <p className="text-xs text-foreground-400">
                    Vous ne trouvez pas ? Affinez votre recherche ou parcourez les rubriques ci-dessus.
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center">
                {searched && query.trim().length >= 2 && (
                  <p className="text-sm text-foreground-500 mb-6">
                    Aucun résultat pour «&nbsp;{query.trim()}&nbsp;». Explorez plutôt nos rubriques :
                  </p>
                )}
                {!searched || query.trim().length < 2 ? (
                  <p className="text-sm text-foreground-500 mb-6">
                    Explorez nos rubriques pour retrouver ce que vous cherchez :
                  </p>
                ) : null}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-2xl mx-auto">
                  {QUICK_LINKS.map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      className="flex flex-col items-center gap-2 p-4 bg-background-50 border border-background-200/70 rounded-lg hover:border-primary-300 hover:bg-primary-50/40 transition-colors no-underline"
                    >
                      <span className="w-10 h-10 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center">
                        <i className={`${l.icon} text-lg`}></i>
                      </span>
                      <span className="text-sm font-medium text-foreground-800">{l.label}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Encart marchand */}
            <div className="bg-background-100 border border-background-200/70 rounded-lg p-4 text-left max-w-2xl mx-auto mt-10">
              <p className="text-xs font-semibold text-foreground-700 mb-2 flex items-center gap-1.5">
                <i className="ri-information-line text-foreground-500"></i>
                Vous cherchez une boutique ?
              </p>
              <p className="text-xs text-foreground-500 leading-relaxed">
                Si vous êtes un commerçant Zifek, retrouvez toutes vos pages depuis votre
                espace marchand. Vos clients accèdent à votre boutique via votre sous-domaine
                ou votre nom de domaine personnalisé.
              </p>
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-700 mt-3 cursor-pointer whitespace-nowrap no-underline"
              >
                Ouvrir mon espace marchand
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* Pied de page */}
      <footer className="border-t border-background-200/70">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-4 text-center">
          <p className="text-xs text-foreground-400">
            &copy; {new Date().getFullYear()} Zifek — La plateforme des commerçants
          </p>
        </div>
      </footer>
    </div>
  );
}